import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import type { HandlerParams, MovementParams } from '@runner/simulation';
import type { PlayLimits, PlaySessionView, SteeringConfig } from '../play/types';
import { validateLimits } from '../play/limits';
import { steeringReady } from '../play/planner';
import { sessionAbi, sessionDomain, sessionTradeTypes, wire } from '../play/protocol';
/**
 * ChainWorld — LOCAL CHAIN mode.
 *
 * Reads come from the RUNNER read API (apps/api): game state, phase and protocol timing (/v1/state), the live feed
 * (SSE /v1/stream with resume and reorg handling), Journal, treasury, rewards, quotes and replay verification. Every
 * response carries `source` (block, lag, stale), which drives an explicit freshness state; when the API or feed is
 * down the view freezes at the last verified snapshot and says so — no motion, countdown or money is invented.
 *
 * Standard writes use the browser wallet (or labelled Anvil key) and go directly to the chain RPC. Explicitly
 * authorized play sessions sign bounded intents with an in-memory key; the separate gas-only relay submits them.
 * Both paths report approval, signature, broadcast, inclusion, replacement and failure stages.
 * Wallet balances (RUNNER, quote, gas) and allowances are read from the chain RPC because they are wallet facts.
 */
import { tradeEvidence } from './tradeEvidence';
import { createPublicClient, encodeFunctionData, http, type Address, type Chain, type EIP1193Provider, type Hash, type Hex, type PublicClient } from 'viem';
import { mockQuoteAssetAbi, runnerPrizesAbi, runnerRewardsAbi, runnerRouterAbi, runnerTokenAbi } from '@runner/sdk/abis';
import { calls, type ContractCall } from '@runner/sdk';
import { economicProfileSummary, initialState, type RunnerState } from '@runner/simulation';
import type { FeedEventDto, StepDto } from '@runner/schema';
import type { WorldSource } from '../WorldProvider';
import type {
  Achievements,
  AllowanceView,
  ConfigSimulation,
  CourseObjectView,
  FeedItem,
  FreshnessView,
  IdentityDetail,
  JournalItem,
  ManifestValidationView,
  OperatorSession,
  QuoteAssetPolicy,
  QuoteView,
  ServerReplayView,
  ShoeStatus,
  StepView,
  TradeRequest,
  TradeSide,
  TxView,
  WaterfallSimRequest,
  WaterfallSimulation,
  WorldView,
} from '../types';
import { CHAIN_ID, DEPLOYMENT_URL, RPC_URL, RUNNER_API, PLAY_SESSIONS_ENABLED } from '../api/config';
import { chainFor, networkInfo } from './chains';
import {
  ApiError,
  RunnerApiClient,
  type AccountRunnersRes,
  type BuybackRes,
  type DeploymentRes,
  type IdentitiesRes,
  type JournalRes,
  type MarketRes,
  type RewardsBreakdownRes,
  type RewardsRes,
  type StateRes,
  type TreasuryRes,
  type VersionsRes,
} from '../api/client';
import { FeedClient } from '../api/feed';
import { connectInjected, devWallet, discoverWallets, isRejection, switchWalletChain, walletChainId, WalletError, type ConnectedWallet } from './wallet';

const ZERO_HASH = `0x${'0'.repeat(64)}`;
const TRADE_GAS = 360_000n;
const APPROVE_GAS = 60_000n;

/** Addresses and parameters the wallet needs, from /v1/deployment (preferred) or the fallback manifest file. */
interface Manifest {
  chainId: number;
  runnerToken: Address;
  quoteAsset: Address;
  router: Address;
  rewards: Address;
  prizes: Address;
  hook: Address;
  identity?: Address;
  wardrobe?: Address;
  treasury?: Address;
  game?: Address;
  registry?: Address;
  genesis: number;
  windowSeconds: number;
  epochSeconds: number;
  hookFeeBps: number;
  launchFeeBps?: number;
  launchFeeSeconds?: number;
  lpFee: number;
  quoteDecimals: number;
  referenceSupply: string;
  quoteSymbol?: string;
  /** LOCAL/TEST economic profile id (fallback manifest file only; the API reports the resolved profile). */
  economicProfile?: string;
}

interface PendingRecord extends TxView {
  from?: string;
  nonce?: number;
}

function manifestFromDeployment(d: DeploymentRes): Manifest {
  const c = d.contracts as Record<string, Address>;
  return {
    chainId: d.chainId,
    runnerToken: c.runnerToken,
    quoteAsset: c.quoteAsset,
    router: c.router,
    rewards: c.rewards,
    prizes: c.prizes,
    hook: c.hook,
    identity: c.identity,
    wardrobe: c.wardrobe,
    treasury: c.treasury,
    game: c.game,
    registry: c.registry,
    genesis: d.genesis,
    windowSeconds: d.windowSeconds,
    epochSeconds: d.epochSeconds,
    hookFeeBps: d.hookFeeBps,
    launchFeeBps: d.launchFeeBps,
    launchFeeSeconds: d.launchFeeSeconds,
    lpFee: d.lpFee,
    quoteDecimals: d.quote.decimals,
    referenceSupply: d.referenceSupply,
    quoteSymbol: d.quote.symbol,
  };
}

function toState(s: StepDto['state']): RunnerState {
  return {
    x: BigInt(s.x),
    y: BigInt(s.y),
    vx: BigInt(s.vx),
    pressure: BigInt(s.pressure),
    route: Number(s.route),
    inSplit: Boolean(s.inSplit),
    slowPct: BigInt(s.slowPct),
    slowSteps: BigInt(s.slowSteps),
    overheatSteps: BigInt(s.overheatSteps),
    stumbleUntilY: BigInt(s.stumbleUntilY),
  };
}

function strip<T extends { source: unknown }>(r: T | null): Omit<T, 'source'> | null {
  if (!r) return null;
  const { source: _s, ...rest } = r;
  void _s;
  return rest;
}

function short(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function fmt(v: bigint, decimals: number, dp = 2): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const base = 10n ** BigInt(decimals);
  const whole = a / base;
  const frac = dp > 0 ? '.' + (((a % base) * 10n ** BigInt(dp)) / base).toString().padStart(dp, '0') : '';
  return `${neg ? '−' : ''}${whole.toLocaleString('en-US')}${frac}`;
}

/** Map one API feed event to a display row. Text comes from the indexer's deterministic summary. */
export function feedItemOf(e: FeedEventDto, you?: string | null, ownTx?: Set<string>): FeedItem {
  const t = e.type;
  const d = e.data as Record<string, unknown>;
  const kind: FeedItem['kind'] = t.startsWith('market.')
    ? 'trade'
    : t === 'run.encounter.v1'
      ? 'encounter'
      : t === 'run.step.v1'
        ? 'step'
        : t.startsWith('run.') || t.startsWith('randomness.') || t.startsWith('road.')
          ? 'run'
          : t.startsWith('prize.')
            ? 'prize'
            : t.startsWith('forge.')
              ? 'forge'
              : t.startsWith('rewards.') || t.startsWith('treasury.')
                ? 'rewards'
                : t.startsWith('identity.') || t.startsWith('wardrobe.')
                  ? 'identity'
                  : 'system';
  const tone: FeedItem['tone'] =
    t === 'chain.reorg.v1'
      ? 'warn'
      : t === 'market.swap.v1'
        ? d.isBuy
          ? 'harvest'
          : 'forge'
        : kind === 'prize'
          ? 'gold'
          : kind === 'forge' || t === 'road.overheat-started.v1' || (t === 'road.effect.v1' && d.to === 'forge')
            ? 'forge'
            : t === 'rewards.deferred-released.v1'
              ? 'water'
              : kind === 'rewards' || (t === 'road.effect.v1' && d.to === 'holders')
                ? 'harvest'
                : 'neutral';
  const lower = (x: unknown) => (typeof x === 'string' ? x.toLowerCase() : '');
  // Yours when the event names your address, or when it came from a transaction this browser sent (sells carry no
  // participant: RunnerRouter only authenticates buyers for Golden Shoe entries).
  const mine =
    (!!you && [d.participant, d.account, d.winner, d.recipient, d.owner, d.from, d.to].some((x) => lower(x) === you.toLowerCase())) || !!ownTx?.has(e.txHash.toLowerCase());
  return {
    id: `seq-${e.seq}`,
    seq: e.seq,
    at: e.at,
    kind,
    text: mine ? `${e.summary} · you` : e.summary,
    tone,
    evidence: e.txHash === ZERO_HASH ? undefined : e.txHash,
    finality: e.finality,
  };
}

export class ChainWorld implements WorldSource {
  readonly api: RunnerApiClient;
  private listeners = new Set<() => void>();
  private client: PublicClient;
  private chain: Chain;
  private version = 0;
  private running = false;
  private timers: ReturnType<typeof setTimeout>[] = [];

  // deployment
  private m: Manifest | null = null;
  private dep: DeploymentRes | null = null;
  private manifestSource: 'api' | 'file' | null = null;
  private initErr: string | null = null;
  private policy: QuoteAssetPolicy = { supported: false, verified: false, note: 'Loading deployment…' };

  // API snapshots
  private st: StateRes | null = null;
  private stepCache = new Map<number, Map<number, StepDto>>();
  private treasury: TreasuryRes | null = null;
  private buyback: BuybackRes | null = null;
  private market: MarketRes | null = null;
  private rewards: RewardsRes | null = null;
  private breakdown: RewardsBreakdownRes | null = null;
  private versions: VersionsRes | null = null;
  private identitiesRes: IdentitiesRes | null = null;
  private runners: AccountRunnersRes | null = null;
  private operator: { token: string; session: OperatorSession } | null = null;
  private slowTick = 0;
  private journalRes: JournalRes | null = null;
  private lastOkAt: number | null = null;
  private apiFailures = 0;
  private apiError: string | null = null;
  private stateBusy = false;
  private slowBusy = false;
  private dirty = new Set<string>();
  private fetchingSteps = new Set<number>();
  private shoeYours = { key: '', weight: 0n };
  private windowYours = { window: -1, yours: 0n, at: 0 };
  private reorgNote: string | null = null;
  private behindSince: number | null = null;

  // feed
  private feed: FeedClient;
  private feedVersion = 0;
  private feedCache: { v: number; you: string | null; items: FeedItem[] } = { v: -1, you: null, items: [] };

  // wallet
  private wallet: ConnectedWallet | null = null;
  private playKey: ReturnType<typeof privateKeyToAccount> | null = null;
  private playOwner: Address | null = null;
  private playSending = false;
  private playAttemptedWindow = -1;
  private steeringConfigs = new Map<number, SteeringConfig>();
  private readonly playRelay = (import.meta.env.VITE_PLAY_RELAY_URL as string | undefined)?.replace(/\/$/, '');
  private connecting = false;
  private walletError: string | null = null;
  private bal = { runner: 0n, quote: 0n, gas: 0n, gasPrice: 0n, supply: 0n, chainTime: 0 };
  private txs: TxView[] = [];
  private detachWallet: (() => void) | null = null;

  constructor(apiBase = RUNNER_API, rpcUrl = RPC_URL, chainId = CHAIN_ID) {
    this.api = new RunnerApiClient(apiBase);
    this.chain = chainFor(chainId, rpcUrl);
    this.client = createPublicClient({ chain: this.chain, transport: http(rpcUrl, { retryCount: 0, timeout: 8000 }) }) as PublicClient;
    this.feed = new FeedClient(this.api, {
      onChange: () => {
        this.feedVersion++;
        this.emit();
      },
      onEvent: (e) => this.onFeedEvent(e),
      onReorg: (ancestor, dropped) => {
        this.reorgNote = `Chain reorganised at block ${ancestor}: ${dropped} event${dropped === 1 ? '' : 's'} above it dropped; corrected events follow.`;
        for (const steps of this.stepCache.values()) for (const [k, s] of steps) if (BigInt(s.blockNumber) > ancestor) steps.delete(k);
        for (const k of ['state', 'journal', 'treasury', 'buyback', 'rewards', 'market', 'identity', 'versions']) this.dirty.add(k);
        this.kick();
      },
    });
    this.resume();
  }

  // ------------------------------------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------------------------------------

  resume() {
    if (this.running) return;
    this.running = true;
    if (!this.m) void this.init();
    else this.startLoops();
  }

  suspend() {
    this.running = false;
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    this.feed.stop();
  }

  stop() {
    this.suspend();
    this.detachWallet?.();
  }

  private later(fn: () => void, ms: number) {
    const t = setTimeout(() => {
      this.timers = this.timers.filter((x) => x !== t);
      if (this.running) fn();
    }, ms);
    this.timers.push(t);
  }

  private async init() {
    // 1. Deployment: the API is the source of truth; a configurable manifest file is the fallback for wallet writes.
    try {
      this.dep = await this.api.deployment();
      this.m = manifestFromDeployment(this.dep);
      this.manifestSource = 'api';
    } catch (e) {
      this.apiError = (e as Error).message;
      this.apiFailures++;
      try {
        const r = await fetch(DEPLOYMENT_URL, { cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        this.m = (await r.json()) as Manifest;
        this.manifestSource = 'file';
      } catch {
        this.initErr = `Deployment unavailable: the API at ${this.api.base} is unreachable and no manifest was found at ${DEPLOYMENT_URL}.`;
        this.emit();
        if (this.running) this.later(() => void this.init(), 3000);
        return;
      }
    }
    // 2. The site is built for one chain (VITE_CHAIN_ID): a deployment on another chain is refused outright, so a
    //    wallet is never asked to sign for the wrong network. Then the RPC must report that chain too.
    if (this.m!.chainId !== this.chain.id) {
      this.initErr = `This site is built for ${this.chain.name} (chain ${this.chain.id}), but the deployment is on chain ${this.m!.chainId}. Transactions are disabled.`;
      this.emit();
      return;
    }
    try {
      const chainId = await this.client.getChainId();
      if (chainId !== this.m!.chainId) {
        this.initErr = `RPC chain ${chainId} does not match deployment chain ${this.m!.chainId}.`;
      } else this.initErr = null;
    } catch {
      this.initErr = null; // RPC down only blocks wallet actions; reads still come from the API
    }
    this.policy = await this.quotePolicy();
    this.restorePending();
    this.emit();
    if (this.running) this.startLoops();
  }

  private startLoops() {
    void this.stateLoop();
    void this.slowLoop();
    this.feed.start();
  }

  /**
   * Quote-asset support: on the local chain the labelled MockQuoteAsset is supported only if the API's declaration
   * matches the token actually deployed (address and decimals). Anywhere else a quote asset must be verified.
   */
  private async quotePolicy(): Promise<QuoteAssetPolicy> {
    const m = this.m!;
    const d = this.dep;
    if (!d) return { supported: true, verified: false, note: 'Deployment from the fallback manifest file; quote asset not cross-checked with the API.' };
    if (d.mode !== 'local' || d.chainId !== 31337) {
      return d.quote.verified
        ? { supported: true, verified: true, note: d.quote.label }
        : { supported: false, verified: false, note: `Unsupported quote asset: ${d.quote.symbol} (${d.quote.address}) is not verified for chain ${d.chainId}. Trading is disabled; watching still works.` };
    }
    if (d.quote.address.toLowerCase() !== m.quoteAsset.toLowerCase()) {
      return { supported: false, verified: false, note: 'Unsupported quote asset: the API declares a different quote token than the deployment.' };
    }
    try {
      const dec = await this.client.readContract({ address: m.quoteAsset, abi: mockQuoteAssetAbi, functionName: 'decimals' });
      if (Number(dec) !== d.quote.decimals) {
        return { supported: false, verified: false, note: `Unsupported quote asset: the chain reports ${dec} decimals, the API declares ${d.quote.decimals}.` };
      }
    } catch {
      /* RPC unavailable: keep the API declaration; wallet actions will fail visibly anyway */
    }
    return { supported: true, verified: false, note: d.quote.label };
  }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.version++;
    for (const l of this.listeners) l();
  }

  private kick() {
    // Debounced immediate refresh after a feed event (state + whatever the event made dirty).
    if (this.dirty.has('kick')) return;
    this.dirty.add('kick');
    setTimeout(() => {
      this.dirty.delete('kick');
      if (!this.running) return;
      if (this.dirty.delete('state')) void this.fetchState();
      void this.fetchSlow(false);
    }, 120);
  }

  // ------------------------------------------------------------------------------------------------
  // Polling
  // ------------------------------------------------------------------------------------------------

  private async stateLoop() {
    await this.fetchState();
    const delay = this.apiFailures >= 2 ? Math.min(5000, 1000 * this.apiFailures) : 1000;
    this.later(() => void this.stateLoop(), delay);
  }

  private async slowLoop() {
    await this.fetchSlow(true);
    this.later(() => void this.slowLoop(), 4000);
  }

  private apiFailed(e: unknown) {
    const err = e as ApiError;
    if (err instanceof ApiError && err.code === 'rate-limited') return; // transient, not an outage
    this.apiFailures++;
    this.apiError = err.message ?? String(e);
  }

  private async fetchState() {
    if (this.stateBusy) return;
    this.stateBusy = true;
    try {
      const st = await this.api.state(100);
      this.ingestState(st);
      this.lastOkAt = Date.now();
      this.apiFailures = 0;
      this.apiError = null;
      if (!this.dep) {
        // Recovered after starting from the fallback manifest: adopt the API's deployment now.
        void this.api
          .deployment()
          .then(async (d) => {
            this.dep = d;
            this.m = manifestFromDeployment(d);
            this.manifestSource = 'api';
            this.policy = await this.quotePolicy();
            this.emit();
          })
          .catch(() => {});
      }
    } catch (e) {
      this.apiFailed(e);
    } finally {
      this.stateBusy = false;
      this.emit();
    }
  }

  private ingestState(st: StateRes) {
    this.st = st;
    const run = st.run;
    if (!run) return;
    let cache = this.stepCache.get(run.runId);
    if (!cache) {
      cache = new Map();
      this.stepCache.set(run.runId, cache);
      // keep memory bounded: only the current and previous run
      for (const id of [...this.stepCache.keys()]) if (id < run.runId - 1) this.stepCache.delete(id);
    }
    const last = st.lastSteps;
    for (const s of last) cache.set(s.step, s);
    // Steps that no longer exist (after a reorg) are removed: the API's list is canonical up to its last step.
    const top = last.length ? last[last.length - 1].step : -1;
    for (const k of [...cache.keys()]) if (k > top && last.length) cache.delete(k);
    const first = last.length ? last[0].step : 0;
    if (first > 0 && !cache.has(0) && !this.fetchingSteps.has(run.runId)) {
      this.fetchingSteps.add(run.runId);
      void this.api
        .steps(run.runId, 0, 200)
        .then((r) => {
          const c = this.stepCache.get(run.runId);
          if (c) for (const s of r.steps) if (!c.has(s.step)) c.set(s.step, s);
          this.emit();
        })
        .catch(() => {})
        .finally(() => this.fetchingSteps.delete(run.runId));
    }
  }

  private async fetchSlow(periodic: boolean) {
    if (!this.m || this.slowBusy) return;
    this.slowBusy = true;
    try {
      const jobs: Promise<unknown>[] = [];
      const want = (k: string) => this.dirty.delete(k) || periodic;
      if (want('treasury'))
        jobs.push(this.api.treasury().then((r) => (this.treasury = r)).catch((e) => this.apiFailed(e)));
      if (want('buyback'))
        jobs.push(this.api.buyback().then((r) => (this.buyback = r)).catch(() => {}));
      if (want('market'))
        jobs.push(this.api.market().then((r) => {
          this.market = r;
          if (r.runnerSupply !== undefined) this.bal.supply = BigInt(r.runnerSupply);
        }).catch(() => {}));
      if ((periodic && this.version % 4 === 0) || this.dirty.delete('journal') || !this.journalRes)
        jobs.push(this.api.journal(40).then((r) => (this.journalRes = r)).catch(() => {}));
      if (periodic) this.slowTick++;
      if ((periodic && this.slowTick % 3 === 1) || this.dirty.delete('versions') || !this.versions)
        jobs.push(this.api.versions().then((r) => (this.versions = r)).catch(() => {}));
      const identityDirty = this.dirty.delete('identity');
      if (periodic || identityDirty || !this.identitiesRes)
        jobs.push(this.api.identities(30).then((r) => (this.identitiesRes = r)).catch(() => {}));
      const w = this.wallet;
      const rewardsDirty = this.dirty.delete('rewards');
      if (w) {
        // Account reads change with deposits, credits, claims, prizes and Runner moves, which all arrive as feed events
        // (rewards.*, identity.*, prize.* mark them dirty): the periodic refresh is only a fallback, every third periodic
        // tick. Each one reads the chain per account, so polling them every tick was most of a wallet's RPC cost
        // (MAINNET_READINESS §3c).
        const fallback = periodic && this.slowTick % 3 === 0;
        const mine = (a?: string) => !!a && a.toLowerCase() === w.address.toLowerCase();
        if (rewardsDirty || fallback || !mine(this.rewards?.account))
          jobs.push(this.api.rewards(w.address).then((r) => (this.rewards = r)).catch(() => {}));
        // History/attribution (indexed, cross-checked against the on-chain claimable at the same block by the API).
        if (rewardsDirty || fallback || !mine(this.breakdown?.account))
          jobs.push(this.api.rewardsBreakdown(w.address).then((r) => (this.breakdown = r)).catch(() => {}));
      }
      if (w && (periodic || identityDirty || !this.runners))
        jobs.push(this.api.accountRunners(w.address).then((r) => (this.runners = r)).catch(() => {}));
      if (w && periodic) jobs.push(this.readWallet(w.address));
      if (w) jobs.push(this.readYours(w.address));
      await Promise.all(jobs);
      this.emit();
    } finally {
      this.slowBusy = false;
    }
  }

  /** Wallet facts straight from the chain: balances, gas and the chain clock used for quote/deadline checks. */
  private async readWallet(a: Address) {
    const m = this.m!;
    try {
      const [runner, quote, gas, gasPrice, block] = await Promise.all([
        this.client.readContract({ address: m.runnerToken, abi: runnerTokenAbi, functionName: 'balanceOf', args: [a] }) as Promise<bigint>,
        this.client.readContract({ address: m.quoteAsset, abi: mockQuoteAssetAbi, functionName: 'balanceOf', args: [a] }) as Promise<bigint>,
        this.client.getBalance({ address: a }),
        this.client.getGasPrice(),
        this.client.getBlock(),
      ]);
      Object.assign(this.bal, { runner, quote, gas, gasPrice, chainTime: Number(block.timestamp) });
    } catch {
      /* RPC unavailable: balances keep their last values */
    }
    if (this.wallet?.kind === 'injected') {
      try {
        const id = await walletChainId(this.wallet.provider);
        if (id !== this.wallet.chainId) this.wallet = { ...this.wallet, chainId: id };
      } catch {
        /* wallet unavailable */
      }
    }
  }

  /** Your Golden Shoe weight (authoritative paged entries) and your net input in the open window (indexed swaps). */
  private async readYours(a: Address) {
    const st = this.st;
    if (!st) return;
    const me = a.toLowerCase();
    const shoe = st.shoe;
    if (shoe) {
      const key = `${shoe.shoeId}:${shoe.entryCount}`;
      if (key !== this.shoeYours.key) {
        try {
          const r = await this.api.shoe(shoe.shoeId, 0, 200);
          let w = 0n;
          for (const e of r.entries) if (e.participant.toLowerCase() === me) w += BigInt(e.weight);
          this.shoeYours = { key, weight: w };
        } catch {
          /* keep previous */
        }
      }
    }
    const win = st.timing.currentWindow;
    if (win !== this.windowYours.window || this.dirty.delete('window') || Date.now() - this.windowYours.at > 8000) {
      try {
        const r = await this.api.window(win);
        let y = 0n;
        for (const s of r.swaps) if ((s.participant ?? s.sender).toLowerCase() === me) y += BigInt(s.runnerDelta);
        this.windowYours = { window: win, yours: y, at: Date.now() };
      } catch {
        /* keep previous */
      }
    }
  }

  private onFeedEvent(e: FeedEventDto) {
    const t = e.type;
    if (t.startsWith('run.') || t.startsWith('road.') || t.startsWith('randomness.') || t === 'game.funding-paused.v1') this.dirty.add('state');
    if (t.startsWith('road.') || t.startsWith('run.funded') || t.startsWith('run.closed')) this.dirty.add('treasury');
    if (t.startsWith('identity.') || t.startsWith('wardrobe.')) {
      this.dirty.add('identity');
      this.dirty.add('rewards');
      this.dirty.add('treasury');
    }
    if (t.startsWith('governance.') || t.startsWith('config.') || t === 'treasury.policy-scheduled.v1' || t === 'identity.renderer-set.v1' || t === 'wardrobe.item-registered.v1') this.dirty.add('versions');
    if (t.startsWith('market.')) {
      this.dirty.add('state');
      this.dirty.add('market');
      this.dirty.add('window');
    }
    if (t.startsWith('treasury.')) this.dirty.add('treasury');
    if (t.startsWith('forge.')) {
      this.dirty.add('buyback');
      this.dirty.add('market');
    }
    if (t.startsWith('rewards.') || t.startsWith('prize.')) {
      this.dirty.add('rewards');
      this.dirty.add('state');
    }
    if (t === 'journal.entry.v1') this.dirty.add('journal');
    this.kick();
  }

  // ------------------------------------------------------------------------------------------------
  // View
  // ------------------------------------------------------------------------------------------------

  private stepViews(runId: number): StepView[] {
    const cache = this.stepCache.get(runId);
    if (!cache) return [];
    const ordered = [...cache.values()].sort((a, b) => a.step - b.step);
    const out: StepView[] = [];
    let before = initialState();
    for (let i = 0; i < ordered.length; i++) {
      const s = ordered[i];
      if (s.step !== i) break; // never show a step whose predecessor is missing
      const state = toState(s.state);
      out.push({
        step: s.step,
        window: s.window,
        net: BigInt(s.net),
        volume: BigInt(s.volume),
        u: BigInt(s.u),
        pace: BigInt(s.pace),
        before,
        state,
        stateHash: s.stateHash,
        encounters: s.encounters.map((e) => ({ objectIndex: e.objectIndex, kind: e.kind, hit: e.hit, xCross: BigInt(e.xCross), amount: BigInt(e.amount) })),
        txHash: s.txHash,
        resolvedAt: s.timestamp,
      });
      before = state;
    }
    return out;
  }

  private freshness(steps: StepView[]): FreshnessView {
    const st = this.st;
    const now = Date.now();
    const src = st?.source;
    const down = !!this.initErr && !st ? true : this.apiFailures >= 2 || (this.lastOkAt !== null && now - this.lastOkAt > 8000);
    const api: FreshnessView['api'] = !st ? (this.apiFailures > 0 ? 'down' : 'connecting') : down ? 'down' : src?.stale ? 'degraded' : 'ok';
    const resolved = st?.run?.stepsResolved ?? 0;
    const missing = st?.run && (st.run.status === 'Active' || st.run.status === 'Finished') ? Math.max(0, resolved - steps.length) : 0;
    const lag = src?.lagBlocks ?? null;
    // The API applies the chain's freshness budget. A fixed five-block cutoff is below a normal poll interval
    // on Robinhood Chain (100 ms blocks). Missing resolved game steps still hold animation independently.
    const behind = api !== 'down' && !!st && (missing > 0 || !!src?.stale);
    if (behind) this.behindSince ??= now;
    else this.behindSince = null;
    const catchingUp = behind && (!!src?.stale || missing > 1 || now - (this.behindSince ?? now) > 3000);
    const feed: FreshnessView['feed'] = this.feed.state;
    let note: string;
    if (this.initErr) note = this.initErr;
    else if (api === 'connecting') note = `Connecting to the RUNNER API at ${this.api.base}…`;
    else if (api === 'down')
      note = st
        ? `API unreachable — showing the last verified state from block ${src?.blockNumber}. Nothing moves until it reconnects.`
        : `API unreachable at ${this.api.base}. No game state is shown until it answers.`;
    else if (catchingUp)
      note =
        missing > 0
          ? `Indexer catching up: ${steps.length} of ${resolved} resolved steps indexed${lag !== null ? `, ${lag} blocks behind` : ''}.`
          : `Indexer catching up${lag !== null ? `: ${lag} blocks behind the chain` : ''}${src?.stale ? ' (source marked stale)' : ''}.`;
    else note = `Live · block ${src?.blockNumber ?? '?'}${lag !== null ? ` · indexer lag ${lag}` : ''}`;
    if (this.reorgNote && api !== 'down') note = `${note} ${this.reorgNote}`;
    return {
      feed,
      api,
      blockNumber: src?.blockNumber ?? null,
      lagBlocks: lag,
      stale: api !== 'ok' || !!src?.stale,
      catchingUp,
      pendingSteps: missing,
      lastOkAt: this.lastOkAt,
      note,
    };
  }

  private feedItems(): FeedItem[] {
    const you = this.wallet?.address ?? null;
    const own = new Set(this.txs.filter((t) => t.hash).map((t) => t.hash!.toLowerCase()));
    const key = `${you}:${[...own].join(',')}`;
    if (this.feedCache.v !== this.feedVersion || this.feedCache.you !== key) {
      const items = this.feed.events.map((e) => feedItemOf(e, you, own)).reverse();
      this.feedCache = { v: this.feedVersion, you: key, items };
    }
    return this.feedCache.items;
  }

  getView(): WorldView {
    const m = this.m;
    const st = this.st;
    const dep = this.dep;
    const dec = m?.quoteDecimals ?? networkInfo(this.chain.id).quote?.decimals ?? 18;
    const run = st?.run ?? null;
    const runId = run?.runId ?? 0;
    const steps = run && (run.status === 'Active' || run.status === 'Finished') ? this.stepViews(runId) : [];
    const fresh = this.freshness(steps);
    const t = st?.timing;
    const tr = this.treasury;
    const bb = this.buyback;
    const rw = this.rewards;
    const shoe = st?.shoe ?? null;
    const course: CourseObjectView[] = (run?.course ?? []).map((o) => ({
      index: o.index,
      kind: o.kind,
      y: BigInt(o.y),
      xL: BigInt(o.xL),
      xR: BigInt(o.xR),
      param: o.funded ? 1n : 0n,
      funded: o.funded,
    }));
    const you = this.wallet?.address?.toLowerCase();
    const shoeIdx = shoe ? shoe.objectIndex : run?.course.findIndex((o) => o.kind === 5) ?? -1;
    const journal: JournalItem[] = (this.journalRes?.entries ?? []).map((e) => ({
      id: e.id,
      at: e.at,
      title: e.title,
      body: e.body,
      kind: e.kind,
      evidence: e.evidence.map((x) => x.txHash).filter((x): x is Hex => !!x),
      label: `${e.author === 'deterministic-template' ? 'Deterministic template' : e.author} · ${e.finality}`,
      finality: e.finality,
      contentHash: e.contentHash,
      onchainTx: e.onchainCommitment?.txHash ?? null,
    }));
    const quoteSymbol = dep?.quote.symbol ?? m?.quoteSymbol ?? networkInfo(this.chain.id).quote?.symbol ?? 'quote';
    const tick = this.market?.tick ?? 0;
    const price = this.market ? Number(this.market.priceQuotePerRunner) : 0;
    const stale = fresh.stale;
    const wal = this.wallet;
    const wrongChain = !!wal && wal.chainId !== this.chain.id;
    const net = networkInfo(this.chain.id);
    // Account data belongs to the connected wallet only (a reconnect to another account must not show stale rows).
    const accountOk = !!wal && !!rw && rw.account.toLowerCase() === wal.address.toLowerCase();
    const runnersOk = !!wal && !!this.runners && this.runners.account.toLowerCase() === wal.address.toLowerCase();
    const breakdownOk = !!wal && !!this.breakdown && this.breakdown.account.toLowerCase() === wal.address.toLowerCase();
    return {
      mode: 'local',
      modeLabel: net.kind === 'local'
        ? `LOCAL CHAIN (Anvil ${net.id}) — real Uniswap v4 PoolManager, ${quoteSymbol} test asset (not a stock token), TEST randomness, disposable keys. Reads via the RUNNER API (${this.api.base}); transactions go from your wallet to the chain.`
        : net.kind === 'mainnet'
          ? `${net.name.toUpperCase()} (chain ${net.id}) — real assets and real money. Reads via the RUNNER API (${this.api.base}); transactions are signed in your wallet and sent to the chain. Not audited.`
          : `${net.name.toUpperCase()} (chain ${net.id}) — test network, test assets. Reads via the RUNNER API (${this.api.base}); transactions go from your wallet to the chain.`,
      networkLabel: m ? `${net.name} · hook ${short(m.hook)} · manifest from ${this.manifestSource === 'api' ? 'API' : 'file'}` : net.name,
      network: net,
      healthy: fresh.api === 'ok' && !this.initErr,
      healthNote: fresh.note,
      quote: { symbol: quoteSymbol, decimals: dec, label: dep?.quote.label ?? (net.kind === 'local' ? 'LOCAL TEST ASSET — not USDG' : net.quote?.label ?? 'Quote asset (not verified by the API)'), address: m?.quoteAsset ?? net.quote?.address },
      runner: { symbol: dep?.runner.symbol ?? 'RUNNER', decimals: dep?.runner.decimals ?? 18, label: dep?.runner.label ?? (net.kind === 'local' ? 'RunnerToken (local deployment)' : 'RunnerToken'), address: m?.runnerToken },
      refSupply: BigInt(dep?.referenceSupply ?? m?.referenceSupply ?? '1000000000000000000000000000'),
      phase: t?.phase ?? 'warm-up',
      stage: t?.stage ?? 'no-run',
      timing: {
        now: t?.now ?? 0,
        currentWindow: t?.currentWindow ?? 0,
        windowSeconds: t?.windowSeconds ?? m?.windowSeconds ?? 8,
        windowEndsAt: t?.windowEndsAt ?? 0,
        nextStepWindow: t?.nextStepWindow ?? null,
        unresolvedClosed: t?.unresolvedClosedWindows ?? 0,
        epoch: t?.epoch ?? 0,
        epochSeconds: m?.epochSeconds ?? 120,
        epochEndsAt: t?.epochEndsAt ?? 0,
        // A countdown is shown only from fresh protocol data; while stale or disconnected there is none.
        countdownTo: stale ? null : (t?.countdownTo ?? null),
        phaseDetail: t?.phaseDetail,
        abandonableAt: t?.abandonableAt ?? null,
      },
      run:
        run && run.status !== 'None'
          ? {
              id: run.runId,
              status: run.status === 'CourseRequested' ? 'awaiting-randomness' : run.status === 'Active' ? 'active' : 'finished',
              abandoned: run.abandoned,
              configVersion: run.configVersion,
              engineVersion: run.engine?.version ?? null,
              engineAddress: run.engine?.address ?? null,
              cohortEpoch: run.cohortEpoch,
              policyId: run.policyId,
              budget: BigInt(run.budget),
              exhibition: run.exhibition,
              exitRoute: run.exitRoute,
              reachedFinish: run.reachedFinish,
              startWindow: run.startWindow,
              maxSteps: run.maxSteps,
              courseLength: run.courseLength,
              course,
              seed: run.seed,
              seedLabel: dep?.randomnessProvider.label ?? 'TEST provider seed (local, deterministic — not production randomness)',
              courseHash: run.courseHash,
              shoe: shoe
                ? {
                    shoeId: Number(shoe.shoeId),
                    objectIndex: shoeIdx,
                    status: shoe.status as ShoeStatus,
                    amount: BigInt(shoe.amount),
                    cutoff: shoe.cutoff,
                    totalWeight: BigInt(shoe.totalWeight),
                    entries: shoe.entryCount,
                    yourWeight: this.shoeYours.key.startsWith(`${shoe.shoeId}:`) ? this.shoeYours.weight : 0n,
                    winner: shoe.winner,
                    winnerLabel: shoe.winner ? (you && shoe.winner.toLowerCase() === you ? 'You' : short(shoe.winner)) : null,
                    paid: shoe.status === 'Claimable' && !!rw && BigInt(rw.prizeOwed) === 0n && BigInt(rw.prizePaid) > 0n && shoe.winner?.toLowerCase() === you,
                  }
                : null,
            }
          : null,
      steps,
      openInput: st?.openWindow
        ? {
            window: st.openWindow.window,
            net: BigInt(st.openWindow.net),
            volume: BigInt(st.openWindow.volume),
            swaps: st.openWindow.swaps,
            yours: this.windowYours.window === st.openWindow.window ? this.windowYours.yours : 0n,
          }
        : null,
      road: st?.road ?? null,
      overheat: st?.overheat ?? null,
      funding: st?.funding ?? null,
      baseRewards: st?.baseRewards ?? null,
      cohort: st?.cohort ?? null,
      replayRunId: st?.replayRunId ?? null,
      treasury: strip(tr),
      buyback: (() => {
        const open = BigInt(bb?.openAuthorized ?? '0');
        const spent = BigInt(bb?.totalQuoteSpent ?? '0');
        const inv = BigInt(bb?.inventory ?? '0');
        const burned = BigInt(bb?.totalBurned ?? '0');
        const lastAuth = bb?.authorizations.reduce<(typeof bb.authorizations)[number] | undefined>((a, x) => (!a || x.id > a.id ? x : a), undefined);
        return {
          authorized: open + spent,
          spent,
          purchased: BigInt(bb?.totalPurchased ?? '0'),
          inventory: inv,
          burned,
          status: inv > 0n ? 'purchased' : open > 0n ? 'authorized' : burned > 0n ? 'burned' : 'none',
          lastNote: lastAuth
            ? `Authorization #${lastAuth.id}: ${fmt(BigInt(lastAuth.spent), dec)} of ${fmt(BigInt(lastAuth.amount), dec)} ${quoteSymbol} spent${lastAuth.closed ? ' (closed)' : ''}. Authorization, purchase and burn are separate onchain transactions.`
            : 'Authorization, purchase and burn are separate onchain transactions.',
        } as WorldView['buyback'];
      })(),
      market: {
        price,
        tick,
        hookFeeBps: this.market?.hookFeeBps ?? m?.hookFeeBps ?? 50,
        launchFee: m?.launchFeeSeconds ? { bps: m.launchFeeBps ?? 0, seconds: m.launchFeeSeconds, genesis: m.genesis } : undefined,
        lpFeePips: this.market?.lpFeePips ?? m?.lpFee ?? 3000,
        runnerSupply: this.bal.supply,
        liquidityNote: 'Real v4 pool on local Anvil; seed liquidity permanently locked',
      },
      user: {
        connected: !!wal,
        address: wal?.address ?? null,
        label: this.connecting ? 'Connecting… (no address until approved)' : wal?.label ?? 'Not connected',
        walletKind: wal?.kind ?? null,
        walletChainId: wal?.chainId ?? null,
        wrongChain,
        gas: this.bal.gas,
        gasPrice: this.bal.gasPrice,
        walletError: this.walletError,
        connecting: this.connecting,
        runner: this.bal.runner,
        quote: this.bal.quote,
      },
      account: accountOk ? strip(rw) : null,
      rewardsBreakdown: breakdownOk ? strip(this.breakdown) : null,
      runners: runnersOk ? strip(this.runners) : null,
      collection: this.identitiesRes?.collection ?? null,
      collectionItems: this.identitiesRes?.identities ?? [],
      versions: strip(this.versions),
      feed: this.feedItems(),
      journal,
      txs: this.txs,
      freshness: fresh,
      quotePolicy: this.policy,
      // The API's resolved profile; from the fallback manifest file the same registry resolves its id.
      economicProfile: dep ? dep.economicProfile : m ? economicProfileSummary({ chainId: m.chainId, economicProfile: m.economicProfile }) : null,
      version: this.version,
    };
  }

  // ------------------------------------------------------------------------------------------------
  // Wallet and writes (browser wallet → chain RPC; the API is never involved)
  // ------------------------------------------------------------------------------------------------

  async connect(kind: 'dev' | 'injected' = 'dev', walletId?: string) {
    this.connecting = true;
    this.walletError = null;
    this.emit();
    try {
      if (kind === 'injected') {
        const ws = discoverWallets();
        if (!ws.length) throw new WalletError('no-wallet', 'No browser wallet found. Install an EIP-1193 wallet extension, or use the labelled local dev key. Watching needs no wallet.');
        this.wallet = await connectInjected(ws.find((x) => x.uuid === walletId) ?? ws[0], this.chain, this.chain.rpcUrls.default.http[0]);
        this.attach(this.wallet);
      } else {
        this.wallet = devWallet(this.chain, this.chain.rpcUrls.default.http[0]);
      }
      this.dirty.add('rewards');
      this.dirty.add('identity');
      this.runners = null;
      this.breakdown = null;
      this.operator = null;
      this.shoeYours = { key: '', weight: 0n };
      this.windowYours = { window: -1, yours: 0n, at: 0 };
    } catch (e) {
      this.walletError = (e as Error).message;
      throw e;
    } finally {
      this.connecting = false;
      this.emit();
      void this.fetchSlow(false);
    }
  }

  private attach(w: ConnectedWallet) {
    this.detachWallet?.();
    if (w.kind !== 'injected') return;
    const p = w.provider as EIP1193Provider & { on?: (ev: string, fn: (x: unknown) => void) => void; removeListener?: (ev: string, fn: (x: unknown) => void) => void };
    const onChain = (hex: unknown) => {
      if (this.wallet?.kind === 'injected') this.wallet = { ...this.wallet, chainId: parseInt(String(hex), 16) };
      this.emit();
    };
    const onAccounts = (accs: unknown) => {
      const list = accs as Address[];
      if (!list?.length) {
        this.disconnect();
        return;
      }
      if (this.wallet?.kind === 'injected' && list[0].toLowerCase() !== this.wallet.address.toLowerCase()) {
        void this.connect('injected').catch(() => {});
      }
    };
    p.on?.('chainChanged', onChain);
    p.on?.('accountsChanged', onAccounts);
    this.detachWallet = () => {
      p.removeListener?.('chainChanged', onChain);
      p.removeListener?.('accountsChanged', onAccounts);
      this.detachWallet = null;
    };
  }

  async switchChain() {
    const w = this.wallet;
    if (!w || w.kind !== 'injected') return;
    this.walletError = null;
    try {
      const id = await switchWalletChain(w.provider, this.chain, this.chain.rpcUrls.default.http[0]);
      this.wallet = { ...w, chainId: id };
    } catch (e) {
      this.walletError = (e as Error).message;
    }
    this.emit();
  }

  dismissWalletError() {
    this.walletError = null;
    this.emit();
  }

  disconnect() {
    this.playKey = null;
    this.playOwner = null;
    this.detachWallet?.();
    this.wallet = null;
    this.rewards = null;
    this.breakdown = null;
    this.runners = null;
    this.operator = null;
    this.walletError = null;
    this.emit();
  }

  private track(tx: PendingRecord) {
    const i = this.txs.findIndex((t) => t.id === tx.id);
    if (i >= 0) this.txs[i] = tx;
    else this.txs.unshift(tx);
    this.txs = this.txs.slice(0, 20);
    try {
      const pending = (this.txs as PendingRecord[]).filter((t) => t.hash && (t.stage === 'broadcast' || t.stage === 'sequencer-accepted'));
      localStorage.setItem('runner.pendingTx', JSON.stringify(pending));
    } catch {
      /* storage unavailable: recovery after reload is best-effort */
    }
    this.emit();
  }

  /**
   * Reload recovery: transactions that were broadcast before a reload are re-checked by hash — never re-sent.
   * Mined → final stage; still in the mempool → keep waiting; gone with its nonce consumed → replaced/dropped.
   */
  private restorePending() {
    let list: PendingRecord[] = [];
    try {
      list = JSON.parse(localStorage.getItem('runner.pendingTx') ?? '[]') as PendingRecord[];
    } catch {
      return;
    }
    for (const t of list) {
      if (!t.hash) continue;
      const base: PendingRecord = { ...t, stage: 'broadcast', detail: 'Recovered after reload — checking the receipt (not re-sent).' };
      this.track(base);
      void this.watchReceipt(base, t.hash as Hash, true);
    }
  }

  private async watchReceipt(base: PendingRecord, hash: Hash, recovered = false): Promise<TxView> {
    let replaced: { hash: Hash; reason: string } | null = null;
    try {
      const r = await this.client.waitForTransactionReceipt({
        hash,
        pollingInterval: 1000,
        timeout: recovered ? 600_000 : 300_000,
        onReplaced: (rep) => {
          replaced = { hash: rep.transaction.hash, reason: rep.reason };
          this.track({ ...base, stage: 'replaced', hash: rep.transaction.hash, detail: `Transaction ${rep.reason} (${short(rep.transaction.hash)}).` });
        },
      });
      const rep = replaced as { hash: Hash; reason: string } | null;
      if (rep && rep.reason !== 'repriced') {
        const tx: PendingRecord = {
          ...base,
          stage: 'replaced',
          hash: rep.hash,
          detail: `Your transaction ${short(hash)} was ${rep.reason} by ${short(rep.hash)} in block ${r.blockNumber}. ${base.label} did NOT execute.`,
        };
        this.track(tx);
        return tx;
      }
      if (r.status !== 'success') {
        const tx: PendingRecord = { ...base, stage: 'failed', hash: r.transactionHash, detail: `Reverted onchain in block ${r.blockNumber}. Nothing was spent except gas.` };
        this.track(tx);
        return tx;
      }
      this.track({ ...base, stage: 'sequencer-accepted', hash: r.transactionHash, detail: `Included in block ${r.blockNumber}.` });
      let finalNote = `Included in block ${r.blockNumber}${rep ? ' (repriced by the wallet)' : ''}. Separate finality has not yet been verified.`;
      try {
        const fin = await this.client.getBlock({ blockTag: 'finalized' });
        if (fin.number >= r.blockNumber) finalNote = `Finalized (finalized tag at block ${fin.number}).`;
      } catch {
        /* tag unsupported */
      }
      const influence = this.m ? tradeEvidence(r.logs, this.m.hook, this.m.prizes) : undefined;
      const tx: PendingRecord = { ...base, influence, stage: 'confirmed', hash: r.transactionHash, detail: `${recovered ? 'Recovered after reload. ' : ''}${finalNote}` };
      this.track(tx);
      for (const k of ['state', 'rewards', 'treasury', 'market', 'window', 'identity']) this.dirty.add(k);
      this.kick();
      return tx;
    } catch (e) {
      const msg = (e as Error).message.split('\n')[0];
      const tx: PendingRecord = { ...base, stage: 'failed', hash, detail: recovered ? `Receipt not found after reload (${msg}). It was not re-sent.` : msg };
      this.track(tx);
      return tx;
    }
  }

  private async send(label: string, gas: bigint, write: () => Promise<Hash>): Promise<TxView> {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
    const w = this.requireWallet();
    const base: PendingRecord = { id, label, stage: 'signature-requested', at: Date.now() / 1000, from: w.address };
    this.track(base);
    // Gas: refuse before asking for a signature when the account cannot pay for it.
    try {
      const [bal, price] = await Promise.all([this.client.getBalance({ address: w.address }), this.client.getGasPrice()]);
      this.bal.gas = bal;
      this.bal.gasPrice = price;
      if (bal < gas * price) {
        const tx: PendingRecord = { ...base, stage: 'failed', detail: `Insufficient ETH for gas: ${label} needs about ${fmt(gas * price, 18, 6)} ETH, the wallet holds ${fmt(bal, 18, 6)} ETH. Nothing was signed.` };
        this.track(tx);
        return tx;
      }
    } catch {
      /* RPC unavailable: the wallet will report the failure */
    }
    let hash: Hash;
    try {
      hash = await write();
    } catch (e) {
      const err = e as { shortMessage?: string; message: string; name?: string };
      const rejected = isRejection(e);
      const msg = (err.shortMessage ?? err.message).split('\n')[0];
      const detail = rejected
        ? 'You rejected the request in your wallet. Nothing was signed or sent.'
        : err.name === 'ChainMismatchError' || /chain/i.test(msg) && /mismatch|does not match/i.test(msg)
          ? `Wrong network: ${msg}. Switch the wallet to ${this.chain.name} (chain ${this.chain.id}).`
          : /insufficient funds/i.test(msg)
            ? `Insufficient ETH for gas: ${msg}`
            : msg;
      const tx: PendingRecord = { ...base, stage: rejected ? 'rejected' : 'failed', detail };
      this.track(tx);
      return tx;
    }
    let nonce: number | undefined;
    try {
      nonce = (await this.client.getTransaction({ hash })).nonce;
    } catch {
      /* pending tx not yet visible */
    }
    const sent: PendingRecord = { ...base, stage: 'broadcast', hash, nonce, detail: 'Broadcast — waiting for inclusion. Reloading will not re-send it.' };
    this.track(sent);
    return this.watchReceipt(sent, hash);
  }

  /**
   * Simulate, then send with a gas margin. Estimation runs against the latest block, but the transaction may be
   * mined in the next control window or accrual period (extra storage writes), so an exact estimate can run out of
   * gas. A revert found by the simulation is reported before any signature is requested.
   */
  private async writeTx(w: ConnectedWallet, req: { address: Address; abi: readonly unknown[]; functionName: string; args: readonly unknown[] }): Promise<Hash> {
    const est = await this.client.estimateContractGas({ ...req, account: w.address } as never);
    const gas = (est * 3n) / 2n + 40_000n;
    return w.client.writeContract({ ...req, account: w.address, chain: this.chain, gas } as never);
  }

  private requireWallet(): ConnectedWallet {
    if (!this.wallet) throw new WalletError('no-account', 'Connect a wallet first.');
    return this.wallet;
  }

  /** Throws a WalletError when the wallet cannot transact on this deployment's chain right now. */
  private async requireChain(): Promise<void> {
    const w = this.requireWallet();
    const want = this.chain.id;
    if (w.kind === 'injected') {
      const id = await walletChainId(w.provider).catch(() => w.chainId);
      if (id !== w.chainId) this.wallet = { ...w, chainId: id };
      if (id !== want) throw new WalletError('wrong-chain', `Wrong network: the wallet is on chain ${id}; RUNNER's deployment is on chain ${want}. Switch networks first.`);
    }
  }

  private failTx(label: string, err: unknown): TxView {
    const tx: TxView = { id: `${Date.now()}-x`, label, stage: 'failed', detail: (err as Error).message, at: Date.now() / 1000 };
    this.track(tx);
    return tx;
  }

  private spenderFor(side: TradeSide) {
    const m = this.m!;
    return side === 'buy' ? { token: m.quoteAsset, symbol: this.dep?.quote.symbol ?? 'quote' } : { token: m.runnerToken, symbol: 'RUNNER' };
  }

  async allowance(side: TradeSide, amount: bigint): Promise<AllowanceView | null> {
    const w = this.wallet;
    if (!w || !this.m) return null;
    const { token } = this.spenderFor(side);
    const current = (await this.client.readContract({ address: token, abi: mockQuoteAssetAbi, functionName: 'allowance', args: [w.address, this.m.router] })) as bigint;
    return { needed: current < amount, current, spender: this.m.router, token };
  }

  private async ensureAllowance(token: Address, spender: Address, amount: bigint, label: string): Promise<TxView | null> {
    const w = this.requireWallet();
    const allowance = (await this.client.readContract({ address: token, abi: mockQuoteAssetAbi, functionName: 'allowance', args: [w.address, spender] })) as bigint;
    if (allowance >= amount) return null;
    return this.send(`Approve ${label} (exact amount)`, APPROVE_GAS, () =>
      this.writeTx(w, { address: token, abi: mockQuoteAssetAbi, functionName: 'approve', args: [spender, amount] }),
    );
  }

  private async chainNow(): Promise<number> {
    try {
      const b = await this.client.getBlock();
      this.bal.chainTime = Number(b.timestamp);
      return Number(b.timestamp);
    } catch {
      return this.st?.timing.now ?? Math.floor(Date.now() / 1000);
    }
  }

  async steeringConfig(): Promise<SteeringConfig> {
    const version = this.st?.run?.configVersion;
    if (version === undefined) throw new Error('No run configuration yet.');
    const cached = this.steeringConfigs.get(version);
    if (cached) return cached;
    const c = await this.api.config(version);
    if (c.source.stale || c.version !== version || ![1, 2].includes(c.engine.version) || c.engine.address.toLowerCase() !== this.st?.run?.engine?.address.toLowerCase()) throw new Error('Verified movement rules unavailable.');
    const movement = Object.fromEntries(Object.entries(c.movement).map(([k,v]) => [k, BigInt(v)])) as unknown as MovementParams;
    const handlers = Object.fromEntries(Object.entries(c.handlers).map(([k,v]) => [k, typeof v === 'boolean' ? v : BigInt(v)])) as unknown as HandlerParams;
    const result = { version, engineVersion: c.engine.version, movement, handlers };
    this.steeringConfigs.set(version, result);
    return result;
  }

  async playSupport() {
    if (!PLAY_SESSIONS_ENABLED) return { supported: false, reason: 'Each trade requires wallet confirmation.' };
    if (!this.m || !this.playRelay) return { supported: false, reason: 'Wallet review is available. Prompt-free play needs a session-enabled deployment and relayer.' };
    try {
      const [version, response] = await Promise.all([
        this.client.readContract({ address: this.m.router, abi: sessionAbi, functionName: 'sessionVersion' }),
        fetch(`${this.playRelay}/health`, { signal: AbortSignal.timeout(5000) }),
      ]);
      const health = await response.json();
      const supported = version === 1n && response.ok && health.chainId === this.chain.id && health.router?.toLowerCase() === this.m.router.toLowerCase() && health.ready === true;
      return { supported, reason: supported ? 'Session trades are submitted by the gas-paying relayer.' : 'This deployment does not have a ready play relayer.' };
    } catch { return { supported: false, reason: 'Prompt-free play is unavailable. Use wallet review.' }; }
  }

  async playStatus(): Promise<PlaySessionView | null> {
    if (!PLAY_SESSIONS_ENABLED) return null;
    if (!this.wallet || !this.m) return null;
    try {
      const s = await this.client.readContract({ address: this.m.router, abi: sessionAbi, functionName: 'playSession', args: [this.wallet.address] });
      if (s.id === 0n) return null;
      const valid = s.key !== '0x0000000000000000000000000000000000000000' && Number(s.limits.expiresAt) > await this.chainNow();
      const localKey = this.playOwner === this.wallet.address && this.playKey?.address.toLowerCase() === s.key.toLowerCase();
      return { id: s.id, expiresAt: Number(s.limits.expiresAt), buyRemaining: s.limits.buyBudget - s.bought, sellRemaining: s.limits.sellBudget - s.sold, maxBuy: s.limits.maxBuy, maxSell: s.limits.maxSell, active: valid && localKey, recoverable: valid && !localKey };
    } catch { return null; }
  }

  async startPlay(l: PlayLimits): Promise<PlaySessionView> {
    if (!PLAY_SESSIONS_ENABLED) throw new Error('Play sessions are disabled in this release.');
    validateLimits(l);
    await this.requireChain();
    if (!(await this.playSupport()).supported) throw new Error('Session support is unavailable on this deployment.');
    if (this.playSending) throw new Error('Wait for the pending action.');
    this.playSending = true;
    const w = this.requireWallet(), m = this.m!;
    const key = privateKeyToAccount(generatePrivateKey());
    try {
      // Locks a 5% adverse-price floor at setup as well as the fresh 1% per-trade slippage limit.
      const buy = l.buyBudget > 0n ? await this.quote({ side: 'buy', amountIn: l.maxBuy }) : null;
      const sell = l.sellBudget > 0n ? await this.quote({ side: 'sell', amountIn: l.maxSell }) : null;
      for (const [token, amount, label] of [[m.quoteAsset, l.buyBudget, 'session buys'], [m.runnerToken, l.sellBudget, 'session sells']] as const) {
        if (amount === 0n) continue;
        const a = await this.ensureAllowance(token, m.router, amount, label);
        if (a && a.stage !== 'confirmed') throw new Error(a.detail ?? 'Session approval was not confirmed.');
        if (this.wallet?.address !== w.address) throw new Error('Wallet changed during setup.');
      }
      await this.requireChain();
      const limits = { expiresAt: BigInt(await this.chainNow() + l.duration), buyBudget: l.buyBudget, sellBudget: l.sellBudget, maxBuy: l.maxBuy, maxSell: l.maxSell,
        minBuyRate: buy ? buy.amountOut * 95n * 10n ** 18n / (buy.amountIn * 100n) : 0n,
        minSellRate: sell ? sell.amountOut * 95n * 10n ** 18n / (sell.amountIn * 100n) : 0n };
      const tx = await this.send('Authorize bounded play session', 250_000n, () => this.writeTx(w, { address: m.router, abi: sessionAbi, functionName: 'authorizeSession', args: [key.address, limits] }));
      if (tx.stage !== 'confirmed') throw new Error(tx.detail ?? 'Session was not authorized.');
      if (this.wallet?.address !== w.address) throw new Error('Wallet changed; revoke this session before starting again.');
      this.playKey = key; this.playOwner = w.address; this.playAttemptedWindow = -1;
      const status = await this.playStatus();
      if (!status?.active) throw new Error('Could not verify the new permission.');
      return status;
    } finally { this.playSending = false; }
  }

  async stopPlay() {
    if (!PLAY_SESSIONS_ENABLED) throw new Error('Play sessions are disabled in this release.');
    // Stop locally immediately; on-chain revocation also invalidates already-signed, not-yet-executed requests.
    this.playKey = null;
    await this.requireChain();
    const w = this.requireWallet();
    const tx = await this.send('Revoke play session', 80_000n, () => this.writeTx(w, { address: this.m!.router, abi: sessionAbi, functionName: 'revokeSession', args: [] }));
    if (tx.stage !== 'confirmed') throw new Error(`Stopped locally. On-chain revocation is still needed: ${tx.detail ?? tx.stage}`);
  }

  async playTrade(req: TradeRequest, q: QuoteView, window: number): Promise<TxView> {
    if (!PLAY_SESSIONS_ENABLED) throw new Error('Play sessions are disabled in this release.');
    if (this.playSending) throw new Error('A session action is pending.');
    this.playSending = true;
    try {
      await this.requireChain();
      const s = await this.playStatus(), v = this.getView(), key = this.playKey;
      if (!s?.active || !key || !this.playRelay || !steeringReady(v) || window !== v.openInput?.window || window === this.playAttemptedWindow) throw new Error('The session or steering window is no longer ready.');
      const cap = req.side === 'buy' ? (s.maxBuy < s.buyRemaining ? s.maxBuy : s.buyRemaining) : (s.maxSell < s.sellRemaining ? s.maxSell : s.sellRemaining);
      if (req.amountIn <= 0n || req.amountIn > cap || q.side !== req.side || q.amountIn !== req.amountIn || q.expiresAt <= v.timing.now || q.priceImpactBps > 200 || q.minOut < q.amountOut * 99n / 100n) throw new Error('The quote exceeds your session limits or has expired.');
      this.playAttemptedWindow = window; // No retries after an ambiguous relay response; reconcile the chain first.
      const trade = { owner: this.requireWallet().address, sessionId: s.id, isBuy: req.side === 'buy', amount: req.amountIn, minOut: q.minOut, window, deadline: BigInt(Math.min(q.expiresAt, s.expiresAt, v.timing.windowEndsAt - 1)) };
      const signature = await key.signTypedData({ domain: sessionDomain(this.chain.id, this.m!.router), types: sessionTradeTypes, primaryType: 'SessionTrade', message: trade });
      const response = await fetch(`${this.playRelay}/trade`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: wire({ trade, signature }), signal: AbortSignal.timeout(20_000) });
      const result = await response.json();
      if (!response.ok || !/^0x[0-9a-fA-F]{64}$/.test(result.hash ?? '')) throw new Error(result.error ?? 'Relay response unavailable. Do not retry this window.');
      const base: PendingRecord = { id: `play-${s.id}-${window}`, label: `Session ${req.side}`, stage: 'broadcast', hash: result.hash, at: Date.now()/1000 };
      this.track(base);
      try {
        // A relay response is not proof of execution. Verify the mined calldata before reporting confirmation.
        await this.client.waitForTransactionReceipt({ hash: result.hash as Hash, pollingInterval: 1000, timeout: 300000 });
        const submitted = await this.client.getTransaction({ hash: result.hash as Hash });
        const expected = encodeFunctionData({ abi: sessionAbi, functionName: 'tradeWithSession', args: [trade, signature] });
        if (submitted.to?.toLowerCase() !== this.m!.router.toLowerCase() || submitted.input.toLowerCase() !== expected.toLowerCase()) throw new Error('The relay returned an unrelated transaction. The steering action is not confirmed.');
        return await this.watchReceipt(base, result.hash as Hash);
      } catch (e) {
        const failed: TxView = { ...base, stage: 'failed', detail: (e as Error).message };
        this.track(failed); return failed;
      }
    } finally { this.playSending = false; }
  }

  async quote(req: TradeRequest): Promise<QuoteView> {
    if (!this.m) throw new Error('Deployment not loaded yet');
    if (!this.policy.supported) throw new Error(this.policy.note);
    const q = await this.api.quote(req.side, req.amountIn, true);
    const buy = req.side === 'buy';
    const amountOut = BigInt(q.amountOut);
    const slippageBps = 100;
    const shoe = this.st?.shoe;
    const shoeOpen = !!shoe && shoe.status === 'Open' && (this.st?.timing.now ?? 0) < shoe.cutoff;
    return {
      side: req.side,
      amountIn: BigInt(q.amountIn),
      amountOut,
      projectFee: BigInt(q.projectFee),
      lpFee: BigInt(q.lpFeeEstimate),
      priceImpactBps: q.priceImpactBps,
      minOut: (amountOut * BigInt(10_000 - slippageBps)) / 10_000n,
      slippageBps,
      expiresAt: q.expiresAt,
      steersLabel: buy ? 'Pushes RUNNER right (toward Harvest)' : 'Pushes RUNNER left (toward Forge)',
      shoeEligible: buy && q.goldenShoeEligible && shoeOpen,
      sourceBlock: q.source.blockNumber,
      route: q.route,
    };
  }

  async trade(req: TradeRequest, q: QuoteView): Promise<TxView> {
    const label = req.side === 'buy' ? 'Buy via RunnerRouter' : 'Sell via RunnerRouter';
    try {
      if (!this.policy.supported) throw new Error(this.policy.note);
      await this.requireChain();
    } catch (e) {
      return this.failTx(label, e);
    }
    const m = this.m!;
    const w = this.requireWallet();
    const now = await this.chainNow();
    if (q.expiresAt < now || (req.validUntil !== undefined && req.validUntil <= now) || (req.steeringWindow !== undefined && (!steeringReady(this.getView()) || req.steeringWindow !== this.getView().openInput?.window))) {
      const tx: TxView = { id: `${Date.now()}-exp`, label, stage: 'expired', detail: `Quote expired at chain time ${q.expiresAt} (now ${now}). Refresh the quote — nothing was signed.`, at: Date.now() / 1000 };
      this.track(tx);
      return tx;
    }
    const { token, symbol } = this.spenderFor(req.side);
    const approval = await this.ensureAllowance(token, m.router, req.amountIn, `${symbol} for RunnerRouter`);
    if (approval && approval.stage !== 'confirmed') return approval;
    const deadline = BigInt(req.validUntil ?? now + 300);
    if (req.validUntil !== undefined && await this.chainNow() >= req.validUntil) return this.failTx(label, new Error('The previewed window closed during approval. Preview the next step; no trade was sent.'));
    return this.send(label, TRADE_GAS, () =>
      this.writeTx(w, {
        address: m.router,
        abi: runnerRouterAbi,
        functionName: 'trade',
        args: [{ isBuy: req.side === 'buy', exactInput: true, amount: req.amountIn, limit: q.minOut, recipient: w.address, deadline }],
      }),
    );
  }

  private async guarded(label: string, fn: () => Promise<TxView>): Promise<TxView> {
    try {
      await this.requireChain();
    } catch (e) {
      return this.failTx(label, e);
    }
    return fn();
  }

  async deposit(amount: bigint): Promise<TxView> {
    return this.guarded('Deposit RUNNER', async () => {
      const m = this.m!;
      const w = this.requireWallet();
      const a = await this.ensureAllowance(m.runnerToken, m.rewards, amount, 'RUNNER for the deposit vault');
      if (a && a.stage !== 'confirmed') return a;
      return this.send('Deposit RUNNER', 200_000n, () =>
        this.writeTx(w, { address: m.rewards, abi: runnerRewardsAbi, functionName: 'deposit', args: [amount] }),
      );
    });
  }

  async withdraw(amount: bigint): Promise<TxView> {
    return this.guarded('Withdraw deposit', async () => {
      const m = this.m!;
      const w = this.requireWallet();
      return this.send('Withdraw deposit', 200_000n, () =>
        this.writeTx(w, { address: m.rewards, abi: runnerRewardsAbi, functionName: 'withdraw', args: [amount] }),
      );
    });
  }

  async claimRewards(): Promise<TxView> {
    return this.guarded('Claim rewards', async () => {
      const m = this.m!;
      const w = this.requireWallet();
      return this.send('Claim rewards', 400_000n, () =>
        this.writeTx(w, { address: m.rewards, abi: runnerRewardsAbi, functionName: 'claim', args: [w.address, 500n] }),
      );
    });
  }

  async claimPrize(): Promise<TxView> {
    return this.guarded('Claim Golden Shoe prize', async () => {
      const m = this.m!;
      const w = this.requireWallet();
      return this.send('Claim Golden Shoe prize', 150_000n, () =>
        this.writeTx(w, { address: m.prizes, abi: runnerPrizesAbi, functionName: 'claimPrize', args: [w.address] }),
      );
    });
  }

  // ------------------------------------------------------------------------------------------------
  // Runner identities (RunnerRewards is the one ledger; RunnerIdentity is the ERC-721; RunnerWardrobe the overlays)
  // ------------------------------------------------------------------------------------------------

  private sdkDeployment() {
    const m = this.m;
    if (!m?.identity || !m.wardrobe) throw new Error('Deployment has no identity contracts (re-deploy the local chain).');
    return {
      rewards: m.rewards,
      identity: m.identity,
      wardrobe: m.wardrobe,
      runnerToken: m.runnerToken,
      treasury: m.treasury ?? m.rewards,
      game: m.game ?? m.rewards,
      registry: m.registry ?? m.rewards,
    };
  }

  private callTx(label: string, gas: bigint, c: ContractCall): Promise<TxView> {
    return this.guarded(label, async () => {
      const w = this.requireWallet();
      return this.send(label, gas, () => this.writeTx(w, c));
    });
  }

  async mintIdentity(): Promise<TxView> {
    return this.guarded('Mint a Runner', async () => {
      const d = this.sdkDeployment();
      const backing = BigInt(this.identitiesRes?.collection.backing ?? (await this.api.identities(1)).collection.backing);
      const a = await this.ensureAllowance(d.runnerToken, d.rewards, backing, 'exactly B RUNNER for the vault');
      if (a && a.stage !== 'confirmed') return a;
      const w = this.requireWallet();
      return this.send('Mint a Runner', 320_000n, () => this.writeTx(w, calls.mintIdentity(d)));
    });
  }

  async convertToIdentity(): Promise<TxView> {
    let d;
    try {
      d = this.sdkDeployment();
    } catch (e) {
      return this.failTx('Convert B of your deposit into a Runner', e);
    }
    return this.callTx('Convert B of your deposit into a Runner', 300_000n, calls.convertToIdentity(d));
  }

  async convertToOrdinary(tokenId: bigint): Promise<TxView> {
    let d;
    try {
      d = this.sdkDeployment();
    } catch (e) {
      return this.failTx(`Convert Runner #${tokenId} back to an ordinary deposit`, e);
    }
    return this.callTx(`Convert Runner #${tokenId} back to an ordinary deposit`, 260_000n, calls.convertToOrdinary(d, tokenId));
  }

  async redeemIdentity(tokenId: bigint): Promise<TxView> {
    let d;
    try {
      d = this.sdkDeployment();
    } catch (e) {
      return this.failTx(`Redeem Runner #${tokenId}`, e);
    }
    return this.callTx(`Redeem Runner #${tokenId}`, 260_000n, calls.redeemIdentity(d, tokenId));
  }

  async transferIdentity(tokenId: bigint, to: string): Promise<TxView> {
    const label = `Transfer Runner #${tokenId}`;
    if (!/^0x[0-9a-fA-F]{40}$/.test(to)) return this.failTx(label, new Error('Recipient must be a 0x-prefixed 20-byte address.'));
    let d;
    try {
      d = this.sdkDeployment();
    } catch (e) {
      return this.failTx(label, e);
    }
    const from = this.wallet?.address;
    if (!from) return this.failTx(label, new WalletError('no-account', 'Connect a wallet first.'));
    if (from.toLowerCase() === to.toLowerCase()) return this.failTx(label, new Error('That is your own address: a self-transfer changes nothing.'));
    return this.callTx(label, 320_000n, calls.transferIdentity(d, from, to as Address, tokenId));
  }

  async equip(tokenId: bigint, slot: number, itemId: number): Promise<TxView> {
    let d;
    try {
      d = this.sdkDeployment();
    } catch (e) {
      return this.failTx(`Equip Runner #${tokenId}`, e);
    }
    return this.callTx(itemId ? `Equip item ${itemId} on Runner #${tokenId}` : `Clear slot ${slot} of Runner #${tokenId}`, 120_000n, calls.equip(d, tokenId, slot, itemId));
  }

  async identity(tokenId: bigint): Promise<IdentityDetail> {
    const r = await this.api.identity(tokenId);
    return strip(r)!;
  }

  async achievements(account: string): Promise<Achievements> {
    return strip(await this.api.achievements(account))!;
  }

  // ------------------------------------------------------------------------------------------------
  // Operator preview (authenticated API; nothing is signed except the session message, nothing is broadcast)
  // ------------------------------------------------------------------------------------------------

  operatorSession(): OperatorSession | null {
    if (!this.operator) return null;
    if (Date.parse(this.operator.session.expiresAt) < Date.now()) {
      this.operator = null;
      return null;
    }
    return this.operator.session;
  }

  async operatorSignIn(): Promise<OperatorSession> {
    const w = this.requireWallet();
    const n = await this.api.operatorNonce(w.address);
    const signature = await w.client.signMessage({ account: w.address, message: n.message } as never);
    const s = await this.api.operatorSession(n.message, signature);
    this.operator = { token: s.token, session: { address: s.address, roles: s.roles, expiresAt: s.expiresAt } };
    this.emit();
    return this.operator.session;
  }

  private token(): string {
    const s = this.operatorSession();
    if (!s || !this.operator) throw new Error('Sign in as an operator first (governance proposer, reviewer or guardian).');
    return this.operator.token;
  }

  async simulateWaterfall(req: WaterfallSimRequest): Promise<WaterfallSimulation> {
    return this.api.simulateWaterfall(this.token(), {
      revenue: req.revenue,
      ...(req.run ? { run: req.run } : {}),
      ...(req.compareLegacy ? { compare: ['LEGACY_V3'] } : {}),
    });
  }

  async simulateConfig(body: unknown): Promise<ConfigSimulation> {
    return this.api.simulateConfig(this.token(), body);
  }

  /** Server-side manifest validation (operator session; the shared @runner/schema rules). */
  async validateManifest(manifest: unknown): Promise<ManifestValidationView> {
    return this.api.validateManifest(this.token(), manifest);
  }

  /** Server-side replay (/v1/verify/run/:id): TS reference engine (the run's own engine) vs on-chain commitments. */
  async verifyRun(runId: number): Promise<ServerReplayView> {
    const r = await this.api.verifyRun(runId);
    return {
      runId: r.runId,
      steps: r.steps,
      matched: r.matched,
      mismatches: r.mismatches.length,
      courseHashMatches: r.courseHashMatches,
      courseRecomputedMatches: r.courseRecomputedMatches,
      inputMismatches: r.inputMismatches.length,
      complete: r.complete,
      engine: r.engine,
      engineVersion: r.engineVersion,
      configVersion: r.configVersion,
      roadEffectsMatch: r.road.effectsMatch,
      escrowMatches: r.road.escrowMatches,
      blockNumber: r.source.blockNumber,
    };
  }
}

export function makeLocalWorld(): WorldSource {
  return new ChainWorld();
}
